<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert, useTrack } from 'dashboard/composables';
import { CAMPAIGN_TYPES } from 'shared/constants/campaign.js';
import { CAMPAIGNS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events.js';

import EmailCampaignForm from 'dashboard/components-next/Campaigns/Pages/CampaignPage/EmailCampaign/EmailCampaignForm.vue';

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();
const formRef = ref(null);

const addCampaign = async campaignDetails => {
  try {
    await store.dispatch('campaigns/create', campaignDetails);

    useTrack(CAMPAIGNS_EVENTS.CREATE_CAMPAIGN, {
      type: CAMPAIGN_TYPES.ONE_OFF,
    });

    useAlert(t('CAMPAIGN.EMAIL.CREATE.FORM.API.SUCCESS_MESSAGE'));
    return true;
  } catch (error) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.response?.message ||
      error?.message ||
      t('CAMPAIGN.EMAIL.CREATE.FORM.API.ERROR_MESSAGE');
    useAlert(errorMessage);
    return false;
  }
};

const handleClose = (options = {}) => {
  const force = options?.force === true;
  if (
    !force &&
    formRef.value?.isDirty &&
    // eslint-disable-next-line no-alert
    !window.confirm(t('CAMPAIGN.EMAIL.CREATE.CONFIRM_DISCARD'))
  ) {
    return;
  }

  emit('close');
};

const handleSubmit = async campaignDetails => {
  const isCreated = await addCampaign(campaignDetails);
  if (isCreated) handleClose({ force: true });
};
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm"
    role="dialog"
    aria-modal="true"
  >
    <div
      data-testid="email-campaign-panel"
      class="flex h-full w-full max-w-[72rem] flex-col overflow-y-auto overscroll-contain border-l border-n-weak bg-n-solid-2 shadow-xl"
    >
      <div
        class="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-n-weak bg-n-solid-2/95 px-6 py-4 backdrop-blur"
      >
        <h3 class="text-base font-medium text-n-slate-12">
          {{ t('CAMPAIGN.EMAIL.CREATE.TITLE') }}
        </h3>
        <button
          type="button"
          class="inline-flex size-8 items-center justify-center rounded-md text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
          :aria-label="t('CAMPAIGN.EMAIL.CREATE.FORM.BUTTONS.CANCEL')"
          @click="handleClose"
        >
          <span class="i-lucide-x size-4" />
        </button>
      </div>
      <div class="px-6 py-5">
        <EmailCampaignForm
          ref="formRef"
          @submit="handleSubmit"
          @cancel="handleClose"
        />
      </div>
    </div>
  </div>
</template>
